import { versionesRecientesPrimero } from "src/app/proyectos/proyecto-pagina/etapas-versiones.util";
import { Version } from "src/app/shared/model/gestion-proyectos/etapa/version.model";

/*
 * Resolución «Pendiente de versión» (T3b; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §5.6 y §8, y
 * docs/PROYECTOS_ETAPAS_VERSIONES.md §5). Funciones puras para los detalles
 * de Desarrollo y de QA, el alta y el reporte. No deciden reglas: el
 * servidor dice en INCIDENCIA_RESOLUCION_ESTADO si la incidencia se resuelve
 * sin elegir versión (modo PENDIENTE_VERSION: proyecto de Mongo, etapa que
 * existe y bandera encendida), si su resolución espera versión (vale con la
 * bandera encendida o apagada) y en qué versión se entregó.
 *
 * El contrato del movimiento no cambia (docs/DEFECTOS.md §2): la versión
 * viaja en id_version_incidencia y, en una incidencia trazable, el servidor
 * la ignora y decide él. El centinela nunca lo manda el front.
 */

/** El centinela de cos_incidencia_detalle_qa.id_version_resuelto (Constantes.VERSION_PENDIENTE del servidor). */
export const VERSION_PENDIENTE = "PENDIENTE_VERSION";
/** Cómo se pinta el centinela (ResolucionPendienteSupport.NOMBRE_PENDIENTE). */
export const NOMBRE_PENDIENTE = "Pendiente de versión";
/** El botón de QA que pasa la incidencia a REVISION (DetIncBotones). */
export const ACCION_REVISION = "En Revision";
/** Por qué «Enviar a revisión» está deshabilitado mientras la resolución espera versión (maqueta Q1). */
export const MOTIVO_REVISION_PENDIENTE = "La corrección todavía no está en ninguna versión";

const EXITO = "00000";
const PREFIJO_RECHAZO = "VAL-";

/** Cómo resuelve Desarrollo: sin elegir versión (T3b) o con el selector de siempre. */
export type ModoResolucion = "PENDIENTE_VERSION" | "SELECCION";

/** ReferenciaVersionBean: solo el id si la versión no está en el proyecto (un id numérico de cos_version). */
export interface ReferenciaVersionResolucion {
  id: string | null;
  nombre: string | null;
  ambiente: string | null;
}

/** ReferenciaEtapaBean: el nombre es null si el id no es una etapa del proyecto. */
export interface ReferenciaEtapaResolucion {
  id: string | null;
  nombre: string | null;
}

/** ResolucionEstadoBean (INCIDENCIA_RESOLUCION_ESTADO). Un rechazo viaja en pCodRetorno/pMensaje. */
export interface ResolucionEstado {
  pCodRetorno: string | null;
  pMensaje: string | null;
  modo: ModoResolucion | null;
  /** RESUELTO con el centinela en su última resolución: QA no la puede enviar a revisión. */
  pendiente: boolean;
  /** La versión de la última resolución, si no es el centinela y la incidencia está en RESUELTO, REVISION o VOBO. */
  versionEntrega: ReferenciaVersionResolucion | null;
  etapa: ReferenciaEtapaResolucion | null;
  versionVigente: ReferenciaVersionResolucion | null;
  versionDetectada: ReferenciaVersionResolucion | null;
}

/** Lo que se lee de la respuesta de un guardado (IncidenciaBean). */
export interface RespuestaMovimiento {
  pCodRetorno?: string | null;
  pMensaje?: string | null;
}

/** Una versión en el selector del alta o del reporte. */
export interface OpcionVersion {
  id: string;
  nombre: string;
  /** «V3-13-08-2026 · Vigente» o «V2-16-07-2026 · Cerrada». */
  etiqueta: string;
  vigente: boolean;
}

/** Si el id es el centinela (con o sin espacios). */
export function esCentinela(idVersion: unknown): boolean {
  return typeof idVersion === "string" && idVersion.trim() === VERSION_PENDIENTE;
}

/** El estado si el servidor lo respondió sin rechazo; null si no (sin estado, nada cambia). */
export function estadoLeido(estado: ResolucionEstado | null | undefined): ResolucionEstado | null {
  return estado?.pCodRetorno === EXITO ? estado : null;
}

/** Desarrollo resuelve sin elegir versión: la resolución se entregará en la siguiente versión QA de la etapa. */
export function resuelveSinSelector(estado: ResolucionEstado | null | undefined): boolean {
  return estadoLeido(estado)?.modo === "PENDIENTE_VERSION";
}

/** La resolución espera versión (independiente de la bandera). */
export function esperaVersion(estado: ResolucionEstado | null | undefined): boolean {
  return estadoLeido(estado)?.pendiente === true;
}

/** «la próxima versión QA de Migración» (D3: una PRODUCTIVO no se la lleva); «de su etapa» si no se sabe cuál. */
export function proximaVersionQA(nombreEtapa: string | null | undefined): string {
  const etapa = (nombreEtapa ?? "").trim();
  return `la próxima versión QA de ${etapa || "su etapa"}`;
}

/**
 * «Entregada en V4-13-10-2026»: solo en una incidencia trazable (modo
 * PENDIENTE_VERSION) con su resolución ya entregada. Con el selector de
 * siempre la versión la eligió Desarrollo y no se dice nada nuevo. Si la
 * versión no está en el proyecto se usa el nombre que ya tiene la ficha.
 */
export function textoEntregada(estado: ResolucionEstado | null | undefined, nombreDeLaFicha: string | null): string | null {
  const entrega = resuelveSinSelector(estado) ? estado?.versionEntrega : null;
  if (!entrega) {
    return null;
  }
  const nombre = entrega.nombre?.trim() || (nombreDeLaFicha ?? "").trim();
  return nombre ? `Entregada en ${nombre}` : null;
}

/**
 * La versión vigente de la etapa para la tarjeta de «Marcar como resuelta»:
 * «V3-13-08-2026 (aquí se detectó)» si es la misma en la que se detectó;
 * «Sin versión vigente» si la etapa no tiene versiones.
 */
export function textoVersionVigente(estado: ResolucionEstado | null | undefined): string {
  const vigente = estadoLeido(estado)?.versionVigente;
  const nombre = vigente?.nombre?.trim();
  if (!nombre) {
    return "Sin versión vigente";
  }
  const detectada = estado?.versionDetectada?.id;
  return vigente.id && vigente.id === detectada ? `${nombre} (aquí se detectó)` : nombre;
}

/**
 * Por qué no se guardó un movimiento (guardaDetalles.do) o una alta
 * (guardarIncidenciaQA.do): el rechazo viaja en el cuerpo con un código VAL-*
 * (p. ej. VAL-RES-PENDIENTE o VAL-INC-VERSION) y su motivo. Sin cuerpo
 * (RestErrorHandler responde 200 vacío ante una falla) tampoco se guardó.
 * null si se guardó.
 */
export function rechazoDelGuardado(respuesta: RespuestaMovimiento | null | undefined): string | null {
  if (!respuesta) {
    return "No se pudo guardar: el servidor no confirmó el movimiento. Vuelve a consultar la incidencia e intenta de nuevo.";
  }
  const codigo = (respuesta.pCodRetorno ?? "").trim();
  if (!codigo.startsWith(PREFIJO_RECHAZO)) {
    return null;
  }
  return respuesta.pMensaje?.trim() || "El servidor rechazó el movimiento. No se guardó nada.";
}

/**
 * La versión con la que viaja un movimiento: la que trae la fila, salvo el
 * centinela, que nunca lo manda el cliente (se manda la detectada y el
 * servidor pone la versión de la resolución viva).
 */
export function versionParaMovimiento(
  idVersion: string | null | undefined,
  idDetectada: string | null | undefined,
): string | null {
  return (esCentinela(idVersion) ? idDetectada : idVersion) ?? null;
}

/**
 * Las versiones de la etapa para elegir la detectada (alta) o la del
 * reporte: de la más nueva a la más vieja, con «· Vigente» en la de mayor
 * orden (de cualquier ambiente, plan §5.1) y «· Cerrada» en las demás. Una
 * versión guardada sin id no se puede elegir y se omite (la vigente se decide
 * antes de omitirla).
 */
export function opcionesDeVersion(versiones: readonly Version[] | null | undefined): OpcionVersion[] {
  return versionesRecientesPrimero((versiones ?? []).filter(Boolean))
    .map((version, indice) => ({ version, vigente: indice === 0 }))
    .filter(({ version }) => !!String(version.id ?? "").trim())
    .map(({ version, vigente }) => {
      const nombre = version.nombre?.trim() || "Versión sin nombre";
      return {
        id: String(version.id),
        nombre,
        etiqueta: `${nombre} · ${vigente ? "Vigente" : "Cerrada"}`,
        vigente,
      };
    });
}

/** Las versiones tal como vienen, sin «Vigente» ni «Cerrada» (el alta del portal de clientes, que no cambia). */
export function opcionesSinEstado(versiones: readonly Version[] | null | undefined): OpcionVersion[] {
  return (versiones ?? [])
    .filter((version) => !!String(version?.id ?? "").trim())
    .map((version) => {
      const nombre = version.nombre?.trim() || "Versión sin nombre";
      return { id: String(version.id), nombre, etiqueta: nombre, vigente: false };
    });
}

/** El id de la vigente entre las opciones; null si la vigente no se puede elegir o no hay versiones. */
export function idVigente(opciones: readonly OpcionVersion[]): string | null {
  return opciones.find((opcion) => opcion.vigente)?.id ?? null;
}
